import DOMPurify from 'dompurify';

export const structuredCloneUtil = <T>(target: T): T => {
  if (typeof window.structuredClone === 'function') {
    return structuredClone(target);
  }
  return JSON.parse(JSON.stringify(target)) as T;
};

/**
 * The native Sanitizer API's `setHTML(html)`. Not yet in this TypeScript version's DOM lib (which
 * only types the non-sanitising `setHTMLUnsafe`), so we declare the minimal shape we call.
 */
interface SetHtmlCapable {
  setHTML: (html: string) => void;
}

/**
 * Sanitises untrusted HTML and attaches it to `element`, replacing its children. Never uses an
 * `innerHTML`-style string sink. Uses the native `setHTML()` Sanitizer API where available (ES2024+ /
 * modern browsers) — it parses and sanitises atomically, closing mutation-XSS gaps — and otherwise
 * falls back to DOMPurify with `RETURN_DOM_FRAGMENT` + `replaceChildren` (node APIs, no string sink).
 */
export const setSanitizedHtml = (element: Element, html: string): void => {
  // Use native Element.setHTML if available (Sanitizer API)
  if ('setHTML' in Element.prototype) {
    (element as unknown as SetHtmlCapable).setHTML(html);
    return;
  }

  // Fallback for browsers without the Sanitizer API
  const fragment = DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true });
  element.replaceChildren(fragment);
};

export const unionSets = <T>(left: Set<T>, right: Set<T>): Set<T> => {
  // Use native Set.union if available (ES2024+)
  if ('union' in Set.prototype) {
    return (left as any).union(right) as Set<T>;
  }

  // Fallback for older browsers
  if (left.size === 0) {
    return new Set(right);
  }
  if (right.size === 0) {
    return new Set(left);
  }
  const result = new Set<T>();
  left.forEach((item) => result.add(item));
  right.forEach((item) => result.add(item));
  return result;
};

export const isDisjointFromSets = <T>(left: Set<T>, right: Set<T>): boolean => {
  // Use native Set.isDisjointFrom if available (ES2024+)
  if ('isDisjointFrom' in Set.prototype) {
    return (left as any).isDisjointFrom(right) as boolean;
  }

  // Fallback for older browsers - optimize by iterating over smaller set
  const smaller = left.size <= right.size ? left : right;
  const larger = left.size <= right.size ? right : left;

  let isDisjoint = true;
  smaller.forEach((item) => {
    if (larger.has(item)) {
      isDisjoint = false;
    }
  });
  return isDisjoint;
};

export const isSubsetOfSets = <T>(left: Set<T>, right: Set<T>): boolean => {
  // Use native Set.isSubsetOf if available (ES2024+)
  if ('isSubsetOf' in Set.prototype) {
    return (left as any).isSubsetOf(right) as boolean;
  }

  // Fallback for older browsers
  if (left.size > right.size) {
    return false;
  }

  let isSubset = true;
  left.forEach((item) => {
    if (!right.has(item)) {
      isSubset = false;
    }
  });
  return isSubset;
};

export const setsIntersection = <T>(left: Set<T>, right: Set<T>): Set<T> => {
  // Use native Set.intersection if available (ES2024+)
  if ('intersection' in Set.prototype) {
    return (left as any).intersection(right) as Set<T>;
  }

  // Fallback for older browsers - optimize by iterating over smaller set
  const smaller = left.size <= right.size ? left : right;
  const larger = left.size <= right.size ? right : left;

  const result = new Set<T>();
  smaller.forEach((item) => {
    if (larger.has(item)) {
      result.add(item);
    }
  });
  return result;
};

export const setsDifference = <T>(left: Set<T>, right: Set<T>): Set<T> => {
  // Use native Set.difference if available (ES2024+)
  if ('difference' in Set.prototype) {
    return (left as any).difference(right) as Set<T>;
  }

  // Fallback for older browsers
  if (left.size === 0) {
    return new Set<T>();
  }
  if (right.size === 0) {
    return new Set(left);
  }

  const result = new Set<T>();
  left.forEach((item) => {
    if (!right.has(item)) {
      result.add(item);
    }
  });
  return result;
};
